/**
 * ReadingPlanViewer - Full reading plan experience
 * Features: Daily readings, progress tracking, catch-up, completion
 */
import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import api from '@/lib/api';
import { cn } from '@/lib/utils';

// UI Components
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';

// Icons
import {
  Calendar, CheckCircle2, Circle, ChevronLeft, ChevronRight,
  BookOpen, Flame, Trophy, Clock, AlertCircle, Play, Pause,
  RotateCcw, PartyPopper, Target
} from 'lucide-react';

// ============== Today's Reading Card ==============
const TodaysReadingCard = ({ plan, day, passages, onComplete, isCompleting, onLogPassage }) => {
  const isCompleted = plan.completed_days?.includes(day.day_number);

  const openPassage = (passage) => {
    const start = passage.chapter_start ?? passage.chapter ?? 1;
    const book = encodeURIComponent(passage.book || 'Genesis');
    const url = `/bible/${book}/${start}`;
    if (typeof onLogPassage === 'function') {
      try { onLogPassage(passage); } catch (_e) { /* best-effort */ }
    }
    window.open(url, '_blank', 'noopener');
  };
  
  return (
    <Card className={cn(
      "transition-all",
      isCompleted ? "bg-emerald-50 border-emerald-200" : "bg-white"
    )}>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={cn(
              "w-10 h-10 rounded-full flex items-center justify-center text-white font-bold",
              isCompleted 
                ? "bg-emerald-500" 
                : "bg-gradient-to-br from-indigo-500 to-purple-600"
            )}>
              {isCompleted ? <CheckCircle2 className="h-5 w-5" /> : day.day_number}
            </div>
            <div>
              <CardTitle className="text-lg">Day {day.day_number}</CardTitle>
              {day.title && (
                <CardDescription>{day.title}</CardDescription>
              )}
            </div>
          </div>
          {isCompleted && (
            <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200">
              Completed
            </Badge>
          )}
        </div>
      </CardHeader>
      
      <CardContent className="space-y-4">
        {/* Passages */}
        <div>
          <h4 className="text-sm font-medium text-slate-700 mb-3">Today's Readings</h4>
          <div className="space-y-2">
            {passages.map((passage, idx) => (
              <div 
                key={idx}
                className={cn(
                  "p-3 rounded-lg border flex items-center justify-between",
                  isCompleted ? "bg-emerald-50/50 border-emerald-200" : "bg-slate-50 border-slate-200"
                )}
              >
                <div className="flex items-center gap-3">
                  <BookOpen className={cn(
                    "h-4 w-4",
                    isCompleted ? "text-emerald-600" : "text-indigo-600"
                  )} />
                  <span className="font-medium text-slate-700">
                    {(() => {
                      const start = passage.chapter_start ?? passage.chapter;
                      const end = passage.chapter_end;
                      const vs = passage.verse_start;
                      const ve = passage.verse_end;
                      let ref = `${passage.book} ${start ?? ''}`;
                      if (end && end !== start) ref += ` - ${end}`;
                      if (vs) ref += `:${vs}`;
                      if (ve && ve !== vs) ref += `-${ve}`;
                      return ref;
                    })()}
                  </span>
                </div>
                <Button 
                  size="sm" 
                  variant="ghost" 
                  className={cn(
                    isCompleted ? "text-emerald-600" : "text-indigo-600"
                  )}
                  onClick={() => openPassage(passage)}
                  data-testid={`reading-passage-open-${idx}`}
                >
                  Read
                </Button>
              </div>
            ))}
          </div>
        </div>

        {/* Devotional Text */}
        {day.devotional_text && (
          <div className="p-4 bg-indigo-50 rounded-lg border border-indigo-100">
            <h4 className="text-sm font-medium text-indigo-900 mb-2">Devotional</h4>
            <p className="text-sm text-indigo-800 leading-relaxed">{day.devotional_text}</p>
          </div>
        )}

        {/* Prayer Prompt */}
        {day.prayer_prompt && (
          <div className="p-4 bg-amber-50 rounded-lg border border-amber-100">
            <h4 className="text-sm font-medium text-amber-900 mb-2">Prayer Prompt</h4>
            <p className="text-sm text-amber-800 leading-relaxed">{day.prayer_prompt}</p>
          </div>
        )}

        {/* Reflection Questions */}
        {day.reflection_questions?.length > 0 && (
          <div>
            <h4 className="text-sm font-medium text-slate-700 mb-2">Reflection Questions</h4>
            <ul className="space-y-2">
              {day.reflection_questions.map((q, idx) => (
                <li key={idx} className="flex items-start gap-2 text-sm text-slate-600">
                  <span className="text-indigo-500 mt-1">•</span>
                  {q}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Complete Button */}
        {!isCompleted && (
          <Button 
            className="w-full bg-emerald-600 hover:bg-emerald-700"
            onClick={() => onComplete(day.day_number)}
            disabled={isCompleting}
          >
            {isCompleting ? (
              <>
                <span className="animate-spin mr-2">⏳</span>
                Marking Complete...
              </>
            ) : (
              <>
                <CheckCircle2 className="h-4 w-4 mr-2" />
                Mark Day Complete
              </>
            )}
          </Button>
        )}
      </CardContent>
    </Card>
  );
};

// ============== Plan Progress Header ==============
const PlanProgressHeader = ({ plan, onBack }) => {
  const progressPercent = plan.completion_percent || 0;
  const completedDays = plan.completed_days?.length || 0;
  const totalDays = plan.duration_days || 30;
  
  return (
    <div className="mb-6">
      <Button variant="ghost" size="sm" onClick={onBack} className="mb-4 -ml-2">
        <ChevronLeft className="h-4 w-4 mr-1" /> Back to Plans
      </Button>
      
      <div className="bg-gradient-to-r from-indigo-600 to-purple-600 rounded-2xl p-6 text-white shadow-lg">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold">{plan.plan_title}</h1>
            <p className="text-white/80 mt-1">
              Day {plan.current_day} of {totalDays}
            </p>
          </div>
          
          <div className="flex items-center gap-4">
            <div className="text-center">
              <div className="flex items-center gap-1.5 text-orange-300">
                <Flame className="h-5 w-5" />
                <span className="text-2xl font-bold">{plan.streak || 0}</span>
              </div>
              <span className="text-xs text-white/70">Streak</span>
            </div>
            <div className="text-center">
              <div className="flex items-center gap-1.5 text-emerald-300">
                <CheckCircle2 className="h-5 w-5" />
                <span className="text-2xl font-bold">{completedDays}</span>
              </div>
              <span className="text-xs text-white/70">Completed</span>
            </div>
          </div>
        </div>
        
        <div className="mt-4">
          <div className="flex items-center justify-between text-sm mb-2">
            <span className="text-white/80">Progress</span>
            <span className="font-medium">{Math.round(progressPercent)}%</span>
          </div>
          <Progress value={progressPercent} className="h-2 bg-white/20" />
        </div>
      </div>
    </div>
  );
};

// ============== Calendar View ==============
const CalendarView = ({ plan, selectedDay, onSelectDay }) => {
  const totalDays = plan.duration_days || 30;
  const completedDays = new Set(plan.completed_days || []);
  const currentDay = plan.current_day || 1;
  
  // Calculate missed days (past days that weren't completed)
  const startDate = plan.start_date ? new Date(plan.start_date) : new Date();
  const today = new Date();
  const daysPassed = Math.floor((today - startDate) / (1000 * 60 * 60 * 24)) + 1;
  
  const getMissedDays = () => {
    const missed = [];
    for (let i = 1; i < Math.min(daysPassed, totalDays + 1); i++) {
      if (!completedDays.has(i)) {
        missed.push(i);
      }
    }
    return missed;
  };
  
  const missedDays = new Set(getMissedDays());

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <Calendar className="h-4 w-4" />
          Plan Calendar
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-7 gap-1.5">
          {Array.from({ length: totalDays }, (_, i) => i + 1).map(day => {
            const isCompleted = completedDays.has(day);
            const isMissed = missedDays.has(day);
            const isCurrent = day === currentDay;
            const isFuture = day > daysPassed;
            const isSelected = day === selectedDay;
            
            return (
              <button
                key={day}
                onClick={() => onSelectDay(day)}
                className={cn(
                  "w-8 h-8 rounded-full flex items-center justify-center text-xs font-medium transition-all",
                  isCompleted && "bg-emerald-500 text-white",
                  isMissed && !isCompleted && "bg-amber-100 text-amber-700 border-2 border-amber-300",
                  isCurrent && !isCompleted && !isMissed && "bg-indigo-500 text-white ring-2 ring-indigo-300",
                  isFuture && !isCompleted && "bg-slate-100 text-slate-400",
                  !isCompleted && !isMissed && !isCurrent && !isFuture && "bg-slate-100 text-slate-600 hover:bg-slate-200",
                  isSelected && "ring-2 ring-indigo-500 ring-offset-2"
                )}
              >
                {isCompleted ? <CheckCircle2 className="h-3.5 w-3.5" /> : day}
              </button>
            );
          })}
        </div>
        
        {/* Legend */}
        <div className="flex flex-wrap gap-4 mt-4 pt-4 border-t text-xs">
          <div className="flex items-center gap-1.5">
            <div className="w-4 h-4 rounded-full bg-emerald-500" />
            <span className="text-slate-600">Completed</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-4 h-4 rounded-full bg-amber-100 border-2 border-amber-300" />
            <span className="text-slate-600">Missed</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-4 h-4 rounded-full bg-indigo-500" />
            <span className="text-slate-600">Today</span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// ============== Main Component ==============
const ReadingPlanViewer = ({ userPlanId, onBack }) => {
  const queryClient = useQueryClient();
  const [selectedDay, setSelectedDay] = useState(null);
  const [showCompletionDialog, setShowCompletionDialog] = useState(false);

  // Fetch user's plan with details
  const { data: myPlans = [] } = useQuery({
    queryKey: ['my-plans'],
    queryFn: () => api.get('/bible/my-plans').then(r => r.data),
  });
  
  const userPlan = myPlans.find(p => p.id === userPlanId);
  
  // Fetch full plan details with days
  const { data: planDetails, isLoading } = useQuery({
    queryKey: ['plan-details', userPlan?.plan_id],
    queryFn: () => api.get(`/bible/plans/${userPlan.plan_id}`).then(r => r.data),
    enabled: !!userPlan?.plan_id,
  });

  // Complete day mutation
  const completeDayMutation = useMutation({
    mutationFn: (dayNumber) => api.post(`/bible/my-plans/${userPlanId}/complete-day`, { day_number: dayNumber }),
    onSuccess: (data) => {
      queryClient.invalidateQueries(['my-plans']);
      toast.success(`Day completed! ${data.data?.streak > 1 ? `🔥 ${data.data.streak} day streak!` : ''}`);
      
      // Check if plan is now complete
      const completedCount = (userPlan?.completed_days?.length || 0) + 1;
      if (completedCount >= (userPlan?.duration_days || 30)) {
        setShowCompletionDialog(true);
      }
    },
    onError: () => toast.error('Failed to mark day complete'),
  });

  // Log a passage open into the reader travel log (best-effort)
  const logPassage = (passage) => {
    if (!userPlanId || !passage?.book) return;
    api.post(`/bible/my-plans/${userPlanId}/reading-log`, {
      day_number: userPlan?.current_day || 1,
      book: passage.book,
      chapter: Number(passage.chapter_start ?? passage.chapter ?? 1),
      verse_start: passage.verse_start ? Number(passage.verse_start) : null,
      verse_end: passage.verse_end ? Number(passage.verse_end) : null,
      seconds: 0,
    }).catch(() => { /* silent */ });
  };

  if (isLoading || !userPlan || !planDetails) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600" />
      </div>
    );
  }

  const currentDay = userPlan.current_day || 1;
  const displayDay = selectedDay || currentDay;
  const dayData = planDetails.days?.find(d => d.day_number === displayDay) || {
    day_number: displayDay,
    title: `Day ${displayDay}`,
    passages: []
  };

  return (
    <div className="space-y-6">
      {/* Header with progress */}
      <PlanProgressHeader plan={userPlan} onBack={onBack} />

      <div className="grid gap-6 lg:grid-cols-3">
        {/* Main content */}
        <div className="lg:col-span-2">
          {/* Day navigation */}
          <div className="flex items-center justify-between mb-4">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setSelectedDay(Math.max(1, displayDay - 1))}
              disabled={displayDay <= 1}
            >
              <ChevronLeft className="h-4 w-4 mr-1" /> Previous
            </Button>
            
            <span className="text-sm font-medium text-slate-600">
              Day {displayDay} of {userPlan.duration_days}
            </span>
            
            <Button
              variant="outline"
              size="sm"
              onClick={() => setSelectedDay(Math.min(userPlan.duration_days, displayDay + 1))}
              disabled={displayDay >= userPlan.duration_days}
            >
              Next <ChevronRight className="h-4 w-4 ml-1" />
            </Button>
          </div>

          {/* Today's reading card */}
          <TodaysReadingCard
            plan={userPlan}
            day={dayData}
            passages={dayData.passages || []}
            onComplete={(day) => completeDayMutation.mutate(day)}
            isCompleting={completeDayMutation.isPending}
            onLogPassage={logPassage}
          />
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          <CalendarView
            plan={userPlan}
            selectedDay={displayDay}
            onSelectDay={setSelectedDay}
          />
          
          {/* Quick jump to today */}
          {selectedDay && selectedDay !== currentDay && (
            <Button 
              variant="outline" 
              className="w-full"
              onClick={() => setSelectedDay(null)}
            >
              <Target className="h-4 w-4 mr-2" /> Jump to Today
            </Button>
          )}
        </div>
      </div>

      {/* Completion Dialog */}
      <Dialog open={showCompletionDialog} onOpenChange={setShowCompletionDialog}>
        <DialogContent className="text-center">
          <div className="py-6">
            <div className="w-20 h-20 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center mx-auto mb-4 animate-bounce">
              <PartyPopper className="h-10 w-10 text-white" />
            </div>
            <DialogTitle className="text-2xl mb-2">Congratulations! 🎉</DialogTitle>
            <DialogDescription className="text-base">
              You've completed the "{userPlan.plan_title}" reading plan! 
              Your dedication to God's Word is inspiring.
            </DialogDescription>
          </div>
          <DialogFooter className="flex-col gap-2">
            <Button onClick={() => setShowCompletionDialog(false)} className="w-full">
              View Certificate
            </Button>
            <Button variant="outline" onClick={onBack} className="w-full">
              Browse More Plans
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ReadingPlanViewer;
